import { Race } from '../types';

const API_URL = process.env.REACT_APP_API_URL || '';

export type TrackRecordChartData = {
  daily: { date: string; dateKey: string; pl: number }[];
  cumulative: { date: string; cumulativePL: number }[];
};

export type TrackRecordSummary = {
  totalRaces: number;
  totalStake: number;
  averageOdds: number;
  wonRaces: number;
  lostRaces: number;
  voidRaces: number;
  winRate: number;
  totalPL: number;
  roi: number;
  asOf: string;
  throughDate: string;
  charts?: TrackRecordChartData;
};

export type TrackRecordRacesResponse = {
  items: Race[];
  nextCursor: string | null;
  totalFiltered: number;
};

const SUCCESS_MESSAGE =
  'Results History has been sent to your email address. Please check spam/junk just in case.';

export async function fetchTrackRecordSummary(): Promise<TrackRecordSummary> {
  const res = await fetch(`${API_URL}/api/marketing/track-record/summary`);
  if (!res.ok) {
    throw new Error('Failed to load track record summary');
  }
  return res.json();
}

export async function verifyTrackRecordAccess(access: string): Promise<{ valid: boolean; emailMasked?: string }> {
  const res = await fetch(
    `${API_URL}/api/marketing/verify-access-token?access=${encodeURIComponent(access)}`
  );
  if (!res.ok) {
    return { valid: false };
  }
  return res.json();
}

export async function fetchTrackRecordRaces(
  access: string,
  options?: { paginated?: boolean; cursor?: string | null; limit?: number }
): Promise<TrackRecordRacesResponse> {
  const params = new URLSearchParams({ access });
  if (options?.paginated) {
    params.set('paginated', '1');
    if (options.cursor) params.set('cursor', options.cursor);
    if (options.limit) params.set('limit', String(options.limit));
  }
  const res = await fetch(`${API_URL}/api/marketing/track-record/races?${params}`);
  if (res.status === 401) {
    throw new Error('ACCESS_EXPIRED');
  }
  if (!res.ok) {
    throw new Error('Failed to load track record');
  }
  return res.json();
}

export async function requestTrackRecordEmail(
  email: string,
  source?: string
): Promise<{ ok: boolean; message: string }> {
  const res = await fetch(`${API_URL}/api/marketing/request-track-record`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, source }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok && res.status === 400) {
    throw new Error(data.error || 'Valid email required');
  }
  return {
    ok: true,
    message: data.message || SUCCESS_MESSAGE,
  };
}

export { SUCCESS_MESSAGE };
